import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { css } from '@emotion/react';
import { FC } from 'react';
import { IMUIAvatarProps, MUIAvatar } from '../../../../../components/Avatar/MUIAvatar';
import { RelationshipScore } from '../../../../../components/RelationshipScore/RelationshipScore';
import { IPersonDataModel } from '../../../../../data-models/person.data-model';
import { getTitlesForCompany } from '../../../utils/peopleHelpers';
import { SocialMedia } from './SocialMedia';

const Container = css`
  display: grid;
  min-width: fit-content;
  min-height: fit-content;
`;

const NameAndTitleContainer = css`
  padding-top: 0.1rem;
  min-width: 0;

  & * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MainInfoContainer = css`
  display: grid;
  column-gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.25rem;
  border-radius: 4px;

  grid-template-columns: min-content 1fr;
`;

/**
 * Puts the avatar in its own column with every detail beside it, as the V2 Leadership design draws
 * it. The default layout only keeps the name and title next to the avatar and lets the location and
 * social icons run full width underneath it.
 */
const besideAvatarContainer = css`
  align-items: start;
  column-gap: 0.75rem;
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr);
`;

/**
 * The design's avatar is a bordered lavender disc; `MUIAvatar` supplies the fill and the radius but
 * no border, and leaves the initials inheriting their colour. `#d5d7eb` is Figma's
 * `border/default/tooltip`, which has no token in `colors`.
 */
const besideAvatarAvatar = {
  border: '1px solid #d5d7eb',
  color: 'rgba(16, 37, 62, 0.87)',
};

const besideAvatarContent = css`
  display: grid;
  min-width: 0;
`;

const besideAvatarNameAndTitle = css`
  border-radius: 4px;
  /* ButtonBase is inline-flex with centred content by default, which indents the name and title
     away from the location and social rows below. Block layout lines all four up on the left. */
  display: block;
  min-width: 0;
  text-align: left;
  width: 100%;
`;

export interface PersonDataProps {
  person: IPersonDataModel;
  avatarSize?: IMUIAvatarProps['size'];
  companyId: number;
  onClick?: () => void;
  /** Lay every detail out to the right of the avatar rather than wrapping beneath it. */
  besideAvatar?: boolean;
}
export const PersonData: FC<PersonDataProps> = ({ person, avatarSize, companyId, onClick, besideAvatar }) => {
  // The V2 layout draws an 80px avatar; the stacked layout keeps its 56px one.
  const size = avatarSize ?? (besideAvatar ? 'xlarge' : 'large');
  const {
    firstName,
    lastName,
    primaryEmail,
    secondaryEmails,
    socials,
    logoUrl,
    relationshipStrengthScoreNormalized,
  } = person;
  const { city = '', state = '', country = '' } = person.locations?.at(0) ?? {};
  const locationString = [city, state, country].filter(Boolean).join(', ');
  const titles = getTitlesForCompany(person, companyId);

  const nameAndTitle = (
    <div css={NameAndTitleContainer}>
      <Typography variant='body2' component='p' color='text.primary'>{`${firstName ?? ''} ${
        lastName ?? ''
      }`}</Typography>
      <Typography variant='caption' component='p' color='text.secondary' noWrap>
        {titles}
      </Typography>
    </div>
  );

  const relationshipScore = relationshipStrengthScoreNormalized ? (
    <RelationshipScore relationshipStrengthScoreNormalized={relationshipStrengthScoreNormalized ?? null} />
  ) : null;

  const location = locationString ? (
    <Stack direction='row' spacing={1} color='text.secondary' minWidth={'1rem'} mb='0.2rem'>
      <PlaceOutlinedIcon fontSize='inherit' htmlColor='inherit' />
      <Typography variant='caption' component='p' noWrap sx={{ minWidth: '0' }}>
        {locationString}
      </Typography>
    </Stack>
  ) : null;

  const socialMedia = (
    <SocialMedia
      companyId={companyId}
      personId={person.id}
      primaryEmail={primaryEmail}
      secondaryEmails={secondaryEmails}
      twitterUrl={socials?.twitter}
      linkedinUrl={socials?.linkedin}
      facebookUrl={socials?.facebook}
      style={{ marginTop: '0' }}
    />
  );

  if (besideAvatar) {
    return (
      <div css={besideAvatarContainer}>
        <MUIAvatar
          size={size}
          nameParts={[firstName ?? '', lastName ?? '']}
          src={logoUrl}
          style={besideAvatarAvatar}
        />
        <div css={besideAvatarContent}>
          <ButtonBase css={besideAvatarNameAndTitle} disabled={!onClick} onClick={onClick} component={'div'}>
            {nameAndTitle}
          </ButtonBase>
          {relationshipScore && <Stack my='0.2rem'>{relationshipScore}</Stack>}
          {location}
          {socialMedia}
        </div>
      </div>
    );
  }

  return (
    <div css={Container}>
      <ButtonBase css={MainInfoContainer} disabled={!onClick} onClick={onClick} component={'div'}>
        <MUIAvatar size={size} nameParts={[firstName ?? '', lastName ?? '']} src={logoUrl} />
        {nameAndTitle}
      </ButtonBase>
      <Stack my='0.2rem'>{relationshipScore}</Stack>
      {location}
      {socialMedia}
    </div>
  );
};
